import { ReactNode } from 'react';

import { useSelector } from 'react-redux';

import { cn } from '@/lib/utils';
import { Attachment } from '@/modules/humanChat/components/Conversation/MessageArea/Attachment/Attachment';
import { buildStoredFileUrl } from '@/modules/humanChat/hooks/useAttachment';
import { selectSessionSource } from '@/modules/tryIt/redux/selectors';
import { AuthorType } from '@/shared/types/chat';
import { ResponseMessage } from '@/shared/types/tryIt';

import { MEDIA_FRAME, responseClasses } from './constants';

type ImageMessage = Extract<ResponseMessage, { type: 'image' }>;

// --- Grouping logic ---

const MAX_IMAGE_GROUP_SIZE = 4;

export type GroupedResponse =
  | { type: 'single'; response: ResponseMessage; index: number }
  | { type: 'image-group'; responses: ImageMessage[]; startIndex: number };

export const groupConsecutiveImages = (responses: ResponseMessage[]): GroupedResponse[] => {
  const result: GroupedResponse[] = [];
  let i = 0;

  while (i < responses.length) {
    // A failed image is emitted as `single` so it renders through
    // ResponseMessages with its error indicator, instead of vanishing into
    // the icon-less image grid.
    if (responses[i].type === 'image' && !responses[i].error_code) {
      const images: ImageMessage[] = [];
      const startIndex = i;

      while (
        i < responses.length &&
        responses[i].type === 'image' &&
        !responses[i].error_code &&
        images.length < MAX_IMAGE_GROUP_SIZE
      ) {
        images.push(responses[i] as ImageMessage);
        i++;
      }

      if (images.length > 1) {
        result.push({ type: 'image-group', responses: images, startIndex });
      } else {
        result.push({ type: 'single', response: images[0], index: startIndex });
      }
    } else {
      result.push({ type: 'single', response: responses[i], index: i });
      i++;
    }
  }

  return result;
};

// --- Image item ---

const ImageItem = ({
  response,
  source,
  authorType,
}: {
  response: ImageMessage;
  source: ReturnType<typeof selectSessionSource>;
  authorType: `${AuthorType}`;
}) =>
  response.id ? (
    <Attachment
      source={source}
      attachment={{ id: response.id, type: 'image', mime_type: 'image/jpeg' }}
      authorType={authorType}
    />
  ) : (
    <img
      src={buildStoredFileUrl(source, response.url, response.name)}
      alt={response.name}
      className="size-full object-contain"
    />
  );

// --- ImageResponse component ---

interface ImageResponseProps {
  responses: ImageMessage[];
  authorType: `${AuthorType}`;
  icon?: ReactNode;
  goToButton?: ReactNode;
  turnRow?: boolean;
}

export const ImageResponse = ({
  responses,
  authorType,
  icon,
  goToButton,
  turnRow = false,
}: ImageResponseProps) => {
  const source = useSelector(selectSessionSource);

  if (responses.length > 1) {
    return (
      <div className={responseClasses}>
        {icon}
        <div className="flex flex-wrap gap-2">
          {responses.map((response) => (
            <div
              key={response.id ?? response.url}
              className={cn(
                'shrink-0 overflow-hidden rounded-xl shadow-sm',
                turnRow ? MEDIA_FRAME : 'size-50'
              )}
            >
              <ImageItem response={response} source={source} authorType={authorType} />
            </div>
          ))}
        </div>
        {goToButton}
      </div>
    );
  }

  const response = responses[0];

  return (
    <div className={responseClasses}>
      {icon}
      <div
        className={cn('overflow-hidden rounded-xl shadow-sm', turnRow ? MEDIA_FRAME : 'max-w-md')}
      >
        <ImageItem response={response} source={source} authorType={authorType} />
      </div>
      {goToButton}
    </div>
  );
};
